import { PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Link } from '@remix-run/react';
import { GQLOrderShippingMethodType, GQLOrderStatusType, GQLOrderTileFragment } from '@theprintbar/graphql';
import { cn, getCurrencyString, getCurrencyValue, getStoreOrderPricing, getWebOrderPricing } from '@theprintbar/shared';

interface OrderPricingProps {
	order: GQLOrderTileFragment;
	staff?: boolean;
	className?: string;
}

export function OrderPricing({ order, staff, className }: OrderPricingProps) {
	const pricing = order.store ? getStoreOrderPricing(order) : getWebOrderPricing(order);

	const systemAdjustments = order.payment?.supplementaryPayments?.filter(sp => sp.isSystemPayment) || [];
	const additionalCharges = order.payment?.supplementaryPayments?.filter(sp => !sp.isSystemPayment) || [];

	const canDiscount = staff && order.status === GQLOrderStatusType.PendingPayment;

	return (
		<dl className={cn('space-y-4 text-sm font-medium', className)}>
			<div className="space-y-2">
				<div className="flex justify-between">
					<dt>
						<h5 className="text-xs uppercase">Subtotal</h5>
					</dt>
					<dd>
						{getCurrencyString(pricing.subtotal + systemAdjustments.reduce((acc, sp) => acc + getCurrencyValue(sp.amount), 0))}
					</dd>
				</div>
				<div className="flex justify-between ">
					<dt>
						<h5 className="uppercase text-xs">Shipping</h5>
					</dt>
					<dd className={cn(!!pricing.shipping && 'text-sm')}>
						{
							// If shipping is free, show free shipping
							order.shippingMethod === GQLOrderShippingMethodType.Pickup
								? 'Free Pickup'
								: !!order.shippingMethod && !pricing.shipping
								? 'Free shipping'
								: getCurrencyString(pricing.shipping)
						}
					</dd>
				</div>
				{!!additionalCharges.length && (
					<>
						<div className="flex justify-between">
							<dt>
								<h5 className="text-xs uppercase">Additional Charges</h5>
							</dt>
							<dd>-</dd>
						</div>
						{additionalCharges.map((sp, i) => (
							<div key={i} className="flex justify-between">
								<dt>
									<h5 className="text-xs italic">{sp.note}</h5>
								</dt>
								<dd>{getCurrencyString(sp.amount)}</dd>
							</div>
						))}
					</>
				)}
				{pricing.discount > 0 && (
					<Link to={canDiscount ? './discount' : '#'} className="flex justify-between">
						<dt className="mr-4">
							<h5 className={cn('text-xs uppercase', canDiscount && 'underline')}>
								{pricing.systemCoupon ? 'System Code' : pricing.couponCode ? 'Coupon Code' : 'Discount'}
								{canDiscount && <PencilIcon className="w-3 h-3 inline-block ml-1" />}
							</h5>
							{pricing.couponCode && (
								<p className="text-sm text-grey">{pricing.systemCoupon ? pricing.couponDescription : pricing.couponCode}</p>
							)}
						</dt>
						<dd>-{getCurrencyString(pricing.discount)}</dd>
					</Link>
				)}
				{canDiscount && !pricing.discount && (
					<Link to="./discount" className="flex justify-between">
						<dt className="mr-4">
							<h5 className="text-xs uppercase underline">Add Discount</h5>
						</dt>
						<dd>
							<PlusIcon className="w-4 h-4" />
						</dd>
					</Link>
				)}
			</div>
			<div className="space-y-2">
				<div className="flex items-center justify-between border-t border-blue pt-4 gap-4">
					<dt>
						<h4 className="text-sm">Amount Paid</h4>
					</dt>
					<dd className="text-blue text-sm">
						<span className="text-xs">AUD</span> {getCurrencyString(pricing.paid)}
					</dd>
				</div>
				{!!pricing.owed && (
					<div className="flex items-center justify-between gap-4">
						<dt>
							<h4 className="text-lg">Outstanding</h4>
						</dt>
						<dd className="text-blue text-lg">
							<span className="text-xs">AUD</span> {getCurrencyString(pricing.owed)}
						</dd>
					</div>
				)}
				{!!pricing.refunds && (
					<div className="flex items-center justify-between gap-4 text-green-600">
						<dt>
							<h4 className="text-sm">Refunded</h4>
						</dt>
						<dd className="text-sm">
							<span className="text-xs">AUD</span> {getCurrencyString(pricing.refunds)}
						</dd>
					</div>
				)}
			</div>
		</dl>
	);
}
